import React, { ReactNode, useContext, useState } from 'react';
import { CircularProgress, IconButton, ListItemIcon, useTheme } from '@mui/material';
import { TreeItem } from '@mui/lab';
import { I_Folder, I_Installation } from 'src/interfaces/InstallationTypes';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import CancelIcon from '@mui/icons-material/Cancel';
import routes from 'src/Resources/routes.json';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { UserContext } from '../../../contexts/userContext';
import { UserType } from '../../../interfaces/UserTypes';
import MoveModal from './MoveModal';
import useIsMobile from '../../../hooks/useIsMobile';

interface CustomTreeItemProps {
  node: I_Installation | I_Folder;
  parent_id: number;
  children?: ReactNode;
}

const useTreeItemStyles = makeStyles((theme) => ({
  label: {
    fontWeight: 'inherit',
    color: 'inherit'
  },
  labelRoot: {
    display: 'flex',
    alignItems: 'center'
  },
  labelIcon: {
    marginRight: -20
  },
  labelText: {
    fontWeight: 'inherit',
    flexGrow: 1
  }
}));

function CustomTreeItem(props: CustomTreeItemProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const classes = useTreeItemStyles();
  const status = props.node.status;
  const navigate = useNavigate();
  const [selected, setSelected] = useState(false);
  const [openMoveModal, setOpenMoveModal] = useState(false);
  const currentLocation = useLocation();
  const { product } = useContext(ProductIdContext);
  const { currentUser } = useContext(UserContext);

  const handleSelectOneInstallation = (): void => {
    let installation = props.node;

    let installation_path =
      installation.product == 0
        ? routes.installations
        : installation.product == 1
        ? routes.salidomo_installations
        : installation.product == 2
        ? routes.sodiohome_installations
        : installation.product == 4
        ? routes.sodistoregrid_installations
        : installation.product == 5
        ? routes.sodistorepro_installations
        : routes.sodistore_installations;

    let folder_path =
      product == 0
        ? routes.installations
        : product == 1
        ? routes.salidomo_installations
        : product == 2
        ? routes.sodiohome_installations
        : product == 4
        ? routes.sodistoregrid_installations
        : product == 5
        ? routes.sodistorepro_installations
        : routes.sodistore_installations;

    if (installation.type != 'Folder') {
      navigate(
        installation_path +
          routes.tree +
          routes.installation +
          installation.id +
          '/' +
          routes.live,
        {
          replace: true
        }
      );
      setSelected(!selected);
    } else {
      navigate(
        folder_path +
          routes.tree +
          routes.folder +
          installation.id +
          '/' +
          routes.information,
        {
          replace: true
        }
      );
      setSelected(false);
    }
  };

  const renderIcon = () => {
    if (props.node.type === 'Folder') {
      return <FolderIcon />;
    } else if (props.node.type === 'Installation') {
      return <InsertDriveFileIcon />;
    }
    return null;
  };

  const itemClasses = [classes.labelRoot];

  return (
    <TreeItem
      nodeId={props.node.id.toString() + props.node.type}
      label={
        <div className={itemClasses.join(' ')}>
          <ListItemIcon
            color="inherit"
            className={classes.labelIcon}
            sx={{ marginLeft: '10px' }}
          >
            {renderIcon()}
          </ListItemIcon>
          <Typography
            fontWeight="bold"
            variant="body2"
            className={classes.labelText}
            sx={{ marginTop: '5px' }}
          >
            {props.node.name}
          </Typography>

          {currentUser.userType === UserType.admin && (
            <div onClick={(e) => e.stopPropagation()}>
              <IconButton
                size="small"
                onClick={() => setOpenMoveModal(true)}
                sx={{ ml: 1 }}
              >
                <DriveFileMoveOutlinedIcon fontSize="small" />
              </IconButton>
              {openMoveModal && (
                <MoveModal
                  node={props.node}
                  onClose={() => setOpenMoveModal(false)}
                />
              )}
            </div>
          )}

          {props.node.type === 'Installation' && (
            <div>
              {(props.node as any).dataCollectionEnabled === false ? (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    marginLeft: isMobile ? '8px' : '17px',
                    backgroundColor: 'grey'
                  }}
                />
              ) : (
                <>
                  {status === -1 ? (
                    <CancelIcon
                      style={{
                        width: '23px',
                        height: '23px',
                        color: 'red',
                        borderRadius: '50%',
                        marginLeft: isMobile ? '8px' : '30px',
                        marginTop: '30px'
                      }}
                    />
                  ) : (
                    ''
                  )}

                  {status === -2 ? (
                    <CircularProgress
                      size={20}
                      sx={{
                        color: '#f7b34d',
                        marginLeft: isMobile ? '8px' : '22px',
                        marginTop: '30px'
                      }}
                    />
                  ) : (
                    ''
                  )}

                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      marginLeft: isMobile ? '8px' : '17px',
                      backgroundColor:
                        status === 2
                          ? 'red'
                          : status === 1
                          ? 'orange'
                          : status === -1 || status === -2
                          ? 'transparent'
                          : 'green'
                    }}
                  />
                </>
              )}
            </div>
          )}
        </div>
      }
      sx={{
        display:
          currentLocation.pathname ===
            routes.salidomo_installations + routes.tree ||
          currentLocation.pathname ===
            routes.sodistore_installations + routes.tree ||
          currentLocation.pathname === routes.installations + routes.tree ||
          currentLocation.pathname ===
            routes.sodiohome_installations + routes.tree ||
          currentLocation.pathname ===
            routes.sodistoregrid_installations + routes.tree ||
          currentLocation.pathname ===
            routes.sodistorepro_installations + routes.tree ||
          currentLocation.pathname.includes('folder')
            ? 'block'
            : 'none',
        '.MuiTreeItem-content': {
          width: 'inherit',

          borderRadius: '4px',
          height: '50px',
          '&:hover': {
            cursor: 'pointer',
            backgroundColor: theme.colors.primary.lighter
          }
        },
        // Phones: give the expand/collapse caret a ~40px-tall touch target and
        // a slightly larger glyph. Desktop keeps the MUI default iconContainer.
        ...(isMobile && {
          '.MuiTreeItem-iconContainer': {
            width: '30px',
            minHeight: '40px',
            alignItems: 'center',
            '& svg': { fontSize: '1.6rem' }
          }
        }),
        backgroundColor: selected ? '#111111' : '#ffffff'
      }}
      onClick={() => handleSelectOneInstallation()}
    >
      {props.children}
    </TreeItem>
  );
}

export default CustomTreeItem;
